import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Monitor, Layers, List, FileText, GitBranch, Grid3x3 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { OverviewViewMode } from './OverviewDialog';

type ExportFormat = 'png' | 'svg';

const viewModeLabels: Record<OverviewViewMode, string> = {
  flat: 'Flat',
  detailed: 'Detailed',
  tree: 'Tree',
  compact: 'Compact',
};

const viewModeIcons: Record<OverviewViewMode, React.ReactNode> = {
  flat: <List className="w-3.5 h-3.5" />,
  detailed: <FileText className="w-3.5 h-3.5" />,
  tree: <GitBranch className="w-3.5 h-3.5" />,
  compact: <Grid3x3 className="w-3.5 h-3.5" />,
};

interface ExportPngDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExportView: (format: ExportFormat) => void;
  onExportAll: (format: ExportFormat, viewMode: OverviewViewMode) => void;
}

export function ExportPngDialog({ open, onOpenChange, onExportView, onExportAll }: ExportPngDialogProps) {
  const [format, setFormat] = useState<ExportFormat>('png');
  const [overviewViewMode, setOverviewViewMode] = useState<OverviewViewMode>('flat');
  const { isAuthenticated } = useAuth();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Export image</DialogTitle>
          <DialogDescription>Välj format och vad du vill exportera</DialogDescription>
        </DialogHeader>

        <Tabs value={format} onValueChange={(v) => setFormat(v as ExportFormat)} className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="png" className="flex-1">PNG</TabsTrigger>
            <TabsTrigger value="svg" className="flex-1">SVG</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-3">
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => { onExportView(format); onOpenChange(false); }}
          >
            <Monitor className="w-6 h-6" />
            <div className="text-sm font-medium">Aktuell arbetsyta</div>
            <div className="text-xs text-muted-foreground">Exportera det som syns på canvas just nu</div>
          </Button>

          {isAuthenticated && (
            <div className="rounded-lg border p-3 flex flex-col gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 w-full"
                onClick={() => { onExportAll(format, overviewViewMode); onOpenChange(false); }}
              >
                <Layers className="w-6 h-6" />
                <div className="text-sm font-medium">Komplett översikt (alla lager)</div>
                <div className="text-xs text-muted-foreground">Exportera hela det nestade systemet som en bild</div>
              </Button>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted-foreground font-medium">Översiktsvy:</span>
                <Tabs value={overviewViewMode} onValueChange={(v) => setOverviewViewMode(v as OverviewViewMode)} className="w-full">
                  <TabsList className="w-full h-8">
                    {(['flat', 'detailed', 'tree', 'compact'] as OverviewViewMode[]).map(mode => (
                      <TabsTrigger key={mode} value={mode} className="flex-1 text-[11px] gap-1 px-1.5">
                        {viewModeIcons[mode]}
                        {viewModeLabels[mode]}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
